import { KeyboardEvent, ReactNode } from 'react';

import { Chip } from '@braskai/ui-kit';

import { Stack, Typography } from '@mui/material';

import { PAYWALL_CARD_BORDER_COLOR, PAYWALL_CARD_SHADOW } from '../lib/paywallContent';

export type PaywallPlanCardProps = {
  name: string;
  chipLabel?: string;
  detailsLeft: string;
  detailsRight?: ReactNode;
  isCustomPricing?: boolean;
  isSelected: boolean;
  priceLabel: ReactNode;
  onSelectPlan: (planName: string) => void;
};

export const PaywallPlanCard = ({
  name,
  chipLabel,
  detailsLeft,
  detailsRight,
  isCustomPricing = false,
  isSelected,
  priceLabel,
  onSelectPlan,
}: PaywallPlanCardProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    onSelectPlan(name);
  };

  return (
    <Stack
      sx={(theme) => ({
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary[500]}`,
          outlineOffset: 2,
        },
        backgroundColor: isSelected ? theme.palette.primary[50] : theme.palette.common.white,
        border: isSelected ? '3px solid' : '1px solid',
        borderColor: isSelected ? theme.palette.primary[500] : PAYWALL_CARD_BORDER_COLOR,
        borderRadius: 16,
        boxShadow: isSelected ? PAYWALL_CARD_SHADOW : 'none',
        cursor: 'pointer',
        position: 'relative',
      })}
      aria-pressed={isSelected}
      data-testid={`paywall-plan-card-${name.toLowerCase().replaceAll(' ', '-')}`}
      gap={24}
      padding={isSelected ? 14 : 16}
      role="button"
      tabIndex={0}
      onClick={() => onSelectPlan(name)}
      onKeyDown={handleKeyDown}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography color="grey.900" variant="title-4-sb">
          {name}
        </Typography>
        {isCustomPricing ? (
          <Typography color="grey.500" variant="body-2-md">
            {priceLabel}
          </Typography>
        ) : (
          <Typography color="grey.900" variant="title-4-sb">
            {priceLabel}
          </Typography>
        )}
      </Stack>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography color="grey.500" variant="body-2">
          {detailsLeft}
        </Typography>
        {detailsRight}
      </Stack>
      {isSelected && chipLabel && (
        <Chip
          label={chipLabel}
          sx={{
            backgroundColor: 'primary.500',
            color: 'common.white',
            position: 'absolute',
            right: 40,
            top: -12,
          }}
        />
      )}
    </Stack>
  );
};
